/**
 * Die Bedeutungsregeln des Entwurfs „Neuentwurf v2".
 *
 * Der Entwurf trägt seine Farblogik in einem eigenen Block mit sich und
 * begründet sie dort selbst:
 *
 * > Zustand als Tintendichte, nicht als Farbe — der Akzent bleibt für Aktion,
 * > Navigation und Überfälligkeit reserviert.
 *
 * Das ist die Regel, die diese Datei durchsetzt. Wer einen Zustand einfärbt,
 * nimmt dem Akzent seine Aussage: Wenn fünf Dinge grün leuchten, heißt Grün
 * nichts mehr. Deshalb steht hier **keine** Zuordnung, die einem gewöhnlichen
 * Zustand eine Farbe gibt — Zustände unterscheiden sich in der Dichte der
 * Tinte, und nur Aufhalter und Fehler dürfen die Warnfarbe nehmen.
 *
 * Klassen statt `style`-Attribute: Die Rollen liegen als Utilities vor
 * (`bg-ruhe`, `border-ring`), und eine feste Klassenliste ist gegen die
 * strikte CSP unempfindlich. Ein `style={{ background: ... }}` wäre hier
 * bequemer und würde die Werte doppelt führen.
 *
 * Der Punkt ist 7×7 Pixel mit einer Haarlinie. Der Entwurf zeichnet sie als
 * `box-shadow: inset 0 0 0 1px`; hier ist es ein `border` — bei
 * `box-sizing: border-box` ist das dasselbe Bild, aber ohne willkürlichen
 * Schattenwert.
 */

/** Zustand einer Aufgabe. Vier Stufen, keine Farbe. */
export type Aufgabenzustand = "offen" | "arbeit" | "abnahme" | "fertig";

/** Schweregrad eines Befundes. Der Entwurf gibt vier, nicht fünf. */
export type Schwere = "hoch" | "mittel" | "niedrig" | "hinweis";

/**
 * Stand eines Laufs oder eines Schritts.
 *
 * Deckt beides ab, weil der Entwurf beides mit demselben Punkt zeichnet: die
 * Zustände eines Auftrags (`laeuft`, `gebremst`, `wartet`, `ausgefuehrt`,
 * `gescheitert`, `abgebrochen`) und die eines Schritts darin (`blockiert`,
 * `uebersprungen`, `bedingung`, `pausiert`, `ausstehend`).
 */
export type Stand =
  | "laeuft"
  | "gebremst"
  | "wartet"
  | "ausgefuehrt"
  | "blockiert"
  | "gescheitert"
  | "abgebrochen"
  | "uebersprungen"
  | "bedingung"
  | "pausiert"
  | "ausstehend";

/** Wie ein Punkt gezeichnet wird: Füllung und Haarlinie. */
export type Punktbild = { fuellung: string; rand: string };

/**
 * Aufgabenzustände.
 *
 * Aufsteigende Dichte: `offen` liegt auf der Ruhefarbe, `arbeit` ist volle
 * Tinte, `abnahme` eine Stufe leiser, `fertig` ist nur noch ein Ring — die
 * Arbeit ist heraus, der Platz bleibt sichtbar.
 */
export const ZUSTAND: Readonly<Record<Aufgabenzustand, Punktbild>> = {
  offen: { fuellung: "bg-ruhe", rand: "border-ruhe" },
  arbeit: { fuellung: "bg-tinte", rand: "border-tinte" },
  abnahme: { fuellung: "bg-tinte4", rand: "border-tinte4" },
  fertig: { fuellung: "bg-transparent", rand: "border-ring" },
};

/**
 * Schweregrade.
 *
 * `hoch` ist der einzige Grad, der den Akzent nimmt — dieselbe Aussage wie
 * eine überfällige Frist: „das verlangt eine Entscheidung". Der Text bleibt
 * bei allen Graden Tinte, außer bei `hoch`; Paket 19 sagt dazu: nicht fünf
 * Farben für fünf Grade, dann trägt keiner mehr.
 */
export const SCHWERE: Readonly<
  Record<Schwere, Punktbild & { text: string }>
> = {
  hoch: { fuellung: "bg-akzent", rand: "border-akzent", text: "text-akzent" },
  mittel: { fuellung: "bg-tinte", rand: "border-tinte", text: "text-tinte" },
  niedrig: { fuellung: "bg-tinte4", rand: "border-tinte4", text: "text-tinte" },
  hinweis: {
    fuellung: "bg-transparent",
    rand: "border-ring",
    text: "text-tinte",
  },
};

/**
 * Laufstände.
 *
 * `label` steht so in der Oberfläche — die Beschriftung gehört zum Zustand,
 * nicht zur Seite, sonst heißt derselbe Stand an zwei Stellen verschieden.
 *
 * `strich` ist die Ausnahme, die der Entwurf ausdrücklich begründet:
 * Übersprungene Kettenglieder werden **durchgestrichen, nicht eingefärbt** —
 * sonst wäre „nicht gelaufen" von „gelaufen" nur an der Helligkeit zu
 * erkennen, und das ist kein Unterschied, auf den man sich verlassen kann.
 */
export const STAND: Readonly<
  Record<Stand, Punktbild & { label: string; text: string; strich: boolean }>
> = {
  laeuft: {
    fuellung: "bg-akzentflaeche",
    rand: "border-akzentflaeche",
    label: "Läuft",
    text: "text-tinte",
    strich: false,
  },
  gebremst: {
    fuellung: "bg-ruhe",
    rand: "border-ruhe",
    label: "Sammelt",
    text: "text-tinte4",
    strich: false,
  },
  wartet: {
    fuellung: "bg-ruhe",
    rand: "border-ruhe",
    label: "In Warteschlange",
    text: "text-tinte4",
    strich: false,
  },
  ausgefuehrt: {
    fuellung: "bg-tinte",
    rand: "border-tinte",
    label: "Sauber",
    text: "text-tinte4",
    strich: false,
  },
  blockiert: {
    fuellung: "bg-warnflaeche",
    rand: "border-warnflaeche",
    label: "Blockiert",
    text: "text-warn",
    strich: false,
  },
  gescheitert: {
    fuellung: "bg-warnflaeche",
    rand: "border-warnflaeche",
    label: "Gescheitert",
    text: "text-warn",
    strich: false,
  },
  abgebrochen: {
    fuellung: "bg-transparent",
    rand: "border-ring",
    label: "Abgebrochen",
    text: "text-tinte4",
    strich: false,
  },
  uebersprungen: {
    fuellung: "bg-transparent",
    rand: "border-ring",
    label: "Übersprungen",
    text: "text-tinte4",
    strich: true,
  },
  bedingung: {
    fuellung: "bg-transparent",
    rand: "border-ring",
    label: "Bedingung offen",
    text: "text-tinte4",
    strich: false,
  },
  pausiert: {
    fuellung: "bg-transparent",
    rand: "border-ring",
    label: "Pausiert",
    text: "text-tinte4",
    strich: false,
  },
  ausstehend: {
    fuellung: "bg-transparent",
    rand: "border-ruhe",
    label: "Ausstehend",
    text: "text-tinte4",
    strich: false,
  },
};

/**
 * Von der Zustandsgruppe der Datenbank auf das Vokabular des Entwurfs.
 *
 * `zustaende.gruppe` führt fünf Werte — `offen`, `in_arbeit`, `abnahme`,
 * `erledigt`, `verworfen` —, der Entwurf zeichnet vier Punkte. Die Abbildung
 * ist die einzige Stelle, an der beide Vokabulare sich berühren.
 *
 * `erledigt` und `verworfen` fallen zusammen: Beide sind aus der Arbeit heraus,
 * und der Entwurf zeigt dafür denselben leeren Ring. Der Unterschied zwischen
 * „geschafft" und „verworfen" steht im Namen des Zustands, der ohnehin daneben
 * steht — er ist keine Sache der Punktdichte.
 */
export function zustandAusGruppe(gruppe: string): Aufgabenzustand {
  switch (gruppe) {
    case "in_arbeit":
      return "arbeit";
    case "abnahme":
      return "abnahme";
    case "erledigt":
    case "verworfen":
      return "fertig";
    default:
      return "offen";
  }
}

/** Ist diese Gruppe aus der Arbeit heraus? Für Fristen und Zählungen. */
export function istAbgeschlossen(gruppe: string): boolean {
  return gruppe === "erledigt" || gruppe === "verworfen";
}

/**
 * Dringlichkeit als drei Punkte, nicht als Wort.
 *
 * Der Entwurf zeigt sie als Reihe: gesetzte Punkte in Tinte, offene in der
 * Ruhefarbe. Drei ist die Obergrenze — mehr Stufen unterscheidet auf sieben
 * Pixeln niemand mehr.
 */
export function prioritaet(stufe: number): readonly string[] {
  return [0, 1, 2].map((i) => (stufe > i ? "bg-tinte2" : "bg-ruhe"));
}

/**
 * Die Nullregel.
 *
 * Steht schon in Paket 19 und gilt überall: *„eine Null ist kein Ereignis und
 * tritt zurück, ein Rückstand meldet sich."* Ein Band aus Nullen in Warnfarbe
 * meldet dauerhaft Alarm und wird übersehen, wenn es einmal zählt.
 *
 * Deshalb entscheidet nicht die Seite, ob eine Zahl warnt, sondern diese
 * Funktion: Null tritt zurück, alles darüber nimmt den gewünschten Ton.
 */
export function zahlenton(wert: number, ton: "warn" | "akzent"): string {
  if (wert === 0) return "text-tinte4";
  return ton === "warn" ? "text-warn" : "text-akzent";
}
